import React, { useState } from 'react';
import useTranslate from '../../../../../customHooks/useTranslate';
import useLanguage from '../../../../../customHooks/useLanguage';
import { useNotification } from '../../../../../contexts/NotificationsContext';
import LateralPanel from '../../../../../muiComponents/LateralPanel';
import IconButton from '../../../../../muiComponents/IconButton';
import { Alert } from '@mui/material';
import Grid from '../../../../../muiComponents/Grid';
import GridChild from '../../../../../muiComponents/GridChild';
import {
    doubleMD_ColumnGrid,
    full_ColumnGrid,
    gridSpacing20,
    gridSpacing30,
} from '../../../../../utils/commonVariables/grid';
import TextInput from '../../../../../muiComponents/TextInput/TextInput';
import Button from '../../../../../muiComponents/Button';
import { useContractDetail } from '../../../../contextsProperty/ContextContractDetail';
import { useODataMultipleQueries } from '../../../../../customHooks/useODataMultipleQueries';
import { readOnlyEntities } from '../../../../../utils/commonVariables/entityKeys';
import { IStaticRateContractStatusType } from '../../../../../types/entities/IStaticRateContractStatusType';
import LoadedOptionsTypeahead from '../../../../../muiComponents/LoadedOptionsTypeahead';
import { IRateScopeModel } from '../../../../../types/entities/IRateScopeModel';
import { ICurrency } from '../../../../../types/entities/ICurrency';
import DateInput from '../../../../../muiComponents/DateInput';
import { IAgencyHotelRelationship } from '../../../../../types/entities/IAgencyHotelRelationship';
import Title from '../../../../components/Title';
import { MAIN_INFO_VALIDATION_INITIAL_STATE } from '../../../../../utils/contractUtils/contractUtils';
import { validateForm } from '../../../../../utils/validateForms';
import LoadingPanel from './LoadingPanel';
import PanelTitle from '../../../yourProperties/Title';
import useUserProfile from '../../../../../customHooks/useUserProfile';
import ReadOnlyUserAlert from '../../../ReadOnlyUserAlert';

const defaultGridProps = { xs: 12, sm: 6, md: 6, lg: 6, xl: 6 };
const defaultDoubleGridProps = { xs: 12, sm: 12, lg: 12, xl: 12 };

interface MainInfoPanelProps {
    open: boolean;
    onClose: () => void;
}

const MainInfoPanel = (props: MainInfoPanelProps) => {
    const { open, onClose } = props;
    const translate = useTranslate();
    const language = useLanguage();
    const { readOnlyUser } = useUserProfile();

    const {
        mainInfoEdition,
        handleChangeMainInfoEdition,
        handleSaveMainInfo,
        isLoadingPanel,
        isNewContract,
        isBlockedContract,
    } = useContractDetail();

    const { setSuccessMessage, setErrorMessage } = useNotification();

    const { statuses } = useODataMultipleQueries({
        queries: [
            {
                key: 'statuses',
                endpoint: {
                    entityName: readOnlyEntities.StaticRateContractStatusType,
                    select: 'Id,Color,LocalizedCaption,Caption,Name',
                    orderBy: 'LocalizedCaption',
                },
                transform: (items: IStaticRateContractStatusType[]) => {
                    return items.map(item => ({
                        id: parseInt(item.Id),
                        label:
                            item.LocalizedCaption || item.Caption || item.Name,
                        color: item.Color,
                    }));
                },
            },
        ],
        dependencies: [language],
        condition: !isNewContract,
    });

    const { supplierHotels, rateScopeModels, currencies } =
        useODataMultipleQueries({
            queries: [
                {
                    key: 'supplierHotels',
                    endpoint: {
                        entityName: readOnlyEntities.SupplierHotel,
                        select: 'Id,Name,IdCurrency',
                        orderBy: 'Name',
                    },
                },
                {
                    key: 'rateScopeModels',
                    endpoint: {
                        entityName: readOnlyEntities.RateScopeModel,
                        select: 'Id,Name,LocalizedCaption,Key,Caption',
                        orderBy: 'LocalizedCaption',
                    },
                    transform: (items: IRateScopeModel[]) =>
                        items.map(item => ({
                            id: item.Id,
                            label:
                                item.LocalizedCaption ||
                                item.Caption ||
                                item.Name,
                            key: item.Key,
                        })),
                },
                {
                    key: 'currencies',
                    endpoint: {
                        entityName: readOnlyEntities.Currency,
                        select: 'Id,Name',
                        orderBy: 'Name',
                    },
                },
            ],
            dependencies: [],
        });

    const { agencies, ratePlans } = useODataMultipleQueries({
        queries: [
            {
                key: 'agencies',
                endpoint: {
                    entityName: readOnlyEntities.AgencyHotelRelationship,
                    select: 'Agency/Id,Agency/Name',
                    orderBy: 'Agency/Name',
                    expand: ['Agency'],
                    filter: [
                        `IdSupplierHotel eq ${mainInfoEdition.idSupplierHotel}`,
                    ],
                },
            },
            {
                key: 'ratePlans',
                endpoint: {
                    entityName: readOnlyEntities.SupplierHotelRatePlan,
                    select: 'Id,Name',
                    orderBy: 'Name',
                    filter: [
                        `Enabled eq true`,
                        `IdSupplierHotel eq ${mainInfoEdition.idSupplierHotel}`,
                    ],
                },
            },
        ],
        dependencies: [mainInfoEdition.idSupplierHotel],
        condition: !!mainInfoEdition.idSupplierHotel,
    });

    const [validatedForm, setValidatedForm] = useState(false);
    const [formErrors, setFormErrors] = useState(
        MAIN_INFO_VALIDATION_INITIAL_STATE
    );

    const fieldsToValidate = {
        ...(!isNewContract && {
            idStatus: {
                value: mainInfoEdition.idStatus,
                validators: ['requiredIdEntity'],
            },
        }),
        name: {
            value: mainInfoEdition.name,
            validators: ['requiredString'],
        },
        idSupplierHotel: {
            value: mainInfoEdition.idSupplierHotel,
            validators: ['requiredIdEntity'],
        },
        idAgency: {
            value: mainInfoEdition.idAgency,
            validators: ['requiredIdEntity'],
        },
        idRatePlan: {
            value: mainInfoEdition.idRatePlan,
            validators: ['requiredIdEntity'],
        },

        idRateScopeModel: {
            value: mainInfoEdition.idRateScopeModel,
            validators: ['requiredIdEntity'],
        },
        idCurrency: {
            value: mainInfoEdition.idCurrency,
            validators: ['requiredIdEntity'],
        },
        activeFrom: {
            value: mainInfoEdition.activeFrom,
            validators: ['requiredDate'],
        },
        activeTo: {
            value: mainInfoEdition.activeTo,
            validators: ['requiredDate'],
        },
    };

    const validateInputsForm = () => {
        setFormErrors(MAIN_INFO_VALIDATION_INITIAL_STATE);
        const errors = validateForm(fieldsToValidate);
        if (errors.length) {
            for (const error of errors) {
                setFormErrors(prev => ({
                    ...prev,
                    [error.inputKey]: error.errorMessage,
                }));
            }
        }
        return errors.length === 0;
    };

    const handleCopyKey = async () => {
        try {
            await navigator.clipboard.writeText(mainInfoEdition.key);
            setSuccessMessage(translate('copied'));
        } catch (error) {
            setErrorMessage(translate('default_server_error'));
            console.log(error);
        }
    };

    return (
        <LateralPanel open={open}>
            {isLoadingPanel ? (
                <LoadingPanel />
            ) : (
                <form className='flex-column height100perc flex-between'>
                    <div className='flex-column  gap-20'>
                        <div className='flex-between flex-align-center contract-panel__title'>
                            <PanelTitle
                                text={'contract_information'}
                                className='text-secondary-color'
                            />
                            <IconButton iconName='Close' onClick={onClose} />
                        </div>
                        {readOnlyUser ? (
                            <ReadOnlyUserAlert text='read_only_contract' />
                        ) : isBlockedContract ? (
                            <Alert severity='info'>
                                {translate('bloqued_information_contract')}
                            </Alert>
                        ) : null}
                        {!isNewContract && (
                            <div className='flex flex-between border-color-block padding-block-8'>
                                <div className='flex-column gap-5'>
                                    <Title>{translate('contract_key')}</Title>
                                    <p>{mainInfoEdition.key}</p>
                                </div>
                                <IconButton
                                    iconName='ContentCopy'
                                    onClick={handleCopyKey}
                                />
                            </div>
                        )}
                    </div>
                    <div className='flex-column gap-20 flex-grow overflow-auto padding-block-20'>
                        <Grid
                            style={{ width: '100%' }}
                            columnSpacing={gridSpacing30}
                            rowSpacing={gridSpacing20}
                            className='border-color-bottom padding-block-20'
                        >
                            {!isNewContract && (
                                <GridChild gridProps={doubleMD_ColumnGrid}>
                                    <LoadedOptionsTypeahead
                                        required
                                        disabled={
                                            statuses.isLoading || readOnlyUser
                                        }
                                        labelText='status'
                                        options={statuses.data || []}
                                        idValueKey='id'
                                        idValue={mainInfoEdition?.idStatus}
                                        handleChange={(
                                            _event: React.SyntheticEvent,
                                            value: {
                                                id: number;
                                                color: string;
                                                label: string;
                                            } | null
                                        ) => {
                                            handleChangeMainInfoEdition({
                                                idStatus: value ? value.id : 0,
                                            });
                                        }}
                                        onBlur={() => {
                                            if (validatedForm) {
                                                validateInputsForm();
                                            }
                                        }}
                                        error={!!formErrors.idStatus}
                                        errorMessage={formErrors.idStatus}
                                    />
                                </GridChild>
                            )}
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <TextInput
                                    id='name'
                                    gridProps={defaultGridProps}
                                    labelText='name'
                                    value={mainInfoEdition.name}
                                    disabled={isLoadingPanel || readOnlyUser}
                                    handleChange={(
                                        e: React.ChangeEvent<HTMLInputElement>
                                    ) =>
                                        handleChangeMainInfoEdition({
                                            name: e.target.value,
                                        })
                                    }
                                    onBlur={() => {
                                        if (validatedForm) {
                                            validateInputsForm();
                                        }
                                    }}
                                    required
                                    error={!!formErrors.name}
                                    errorMessage={formErrors.name}
                                />
                            </GridChild>
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <LoadedOptionsTypeahead
                                    required
                                    disabled={
                                        supplierHotels.isLoading ||
                                        isBlockedContract ||
                                        readOnlyUser
                                    }
                                    labelText='hotel'
                                    options={supplierHotels.data || []}
                                    idValue={mainInfoEdition?.idSupplierHotel}
                                    handleChange={(
                                        _event: React.SyntheticEvent,
                                        value: {
                                            Id: string;
                                            Name: string;
                                            IdCurrency: number;
                                        } | null
                                    ) => {
                                        handleChangeMainInfoEdition({
                                            idSupplierHotel: value
                                                ? parseInt(value.Id)
                                                : 0,
                                            idCurrency: value
                                                ? value.IdCurrency
                                                : 0,
                                        });
                                    }}
                                    onBlur={() => {
                                        if (validatedForm) {
                                            validateInputsForm();
                                        }
                                    }}
                                    error={!!formErrors.idSupplierHotel}
                                    errorMessage={formErrors.idSupplierHotel}
                                />
                            </GridChild>
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <LoadedOptionsTypeahead
                                    required
                                    disabled={
                                        agencies.isLoading ||
                                        !mainInfoEdition.idSupplierHotel ||
                                        isBlockedContract ||
                                        readOnlyUser
                                    }
                                    labelText='agency'
                                    options={
                                        agencies.data.map(
                                            (
                                                agency: IAgencyHotelRelationship
                                            ) => {
                                                return {
                                                    Id: agency.Agency.Id,
                                                    Name: agency.Agency.Name,
                                                };
                                            }
                                        ) || []
                                    }
                                    optionsLabelKey='Name'
                                    idValue={mainInfoEdition.idAgency}
                                    handleChange={(
                                        _event: React.SyntheticEvent,
                                        value: {
                                            Id: string;
                                            Name: string;
                                        } | null
                                    ) => {
                                        handleChangeMainInfoEdition({
                                            idAgency: value
                                                ? parseInt(value.Id)
                                                : 0,
                                        });
                                    }}
                                    onBlur={() => {
                                        if (validatedForm) {
                                            validateInputsForm();
                                        }
                                    }}
                                    error={!!formErrors.idAgency}
                                    errorMessage={formErrors.idAgency}
                                />
                            </GridChild>
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <LoadedOptionsTypeahead
                                    required
                                    disabled={
                                        ratePlans.isLoading ||
                                        !mainInfoEdition.idSupplierHotel ||
                                        isBlockedContract ||
                                        readOnlyUser
                                    }
                                    labelText='rate_plan'
                                    options={ratePlans.data || []}
                                    idValue={mainInfoEdition?.idRatePlan}
                                    handleChange={(
                                        _event: React.SyntheticEvent,
                                        value: {
                                            Id: string;
                                            Name: string;
                                        } | null
                                    ) => {
                                        handleChangeMainInfoEdition({
                                            idRatePlan: value
                                                ? parseInt(value.Id)
                                                : 0,
                                        });
                                    }}
                                    onBlur={() => {
                                        if (validatedForm) {
                                            validateInputsForm();
                                        }
                                    }}
                                    error={!!formErrors.idRatePlan}
                                    errorMessage={formErrors.idRatePlan}
                                />
                            </GridChild>
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <LoadedOptionsTypeahead
                                    required
                                    disabled={
                                        rateScopeModels.isLoading ||
                                        isBlockedContract ||
                                        readOnlyUser
                                    }
                                    labelText='rate_scope_model'
                                    options={rateScopeModels.data || []}
                                    idValue={mainInfoEdition?.idRateScopeModel}
                                    idValueKey='id'
                                    handleChange={(
                                        _event: React.SyntheticEvent,
                                        value: {
                                            id: string;
                                            label: string;
                                        } | null
                                    ) => {
                                        handleChangeMainInfoEdition({
                                            idRateScopeModel: value
                                                ? parseInt(value.id)
                                                : 0,
                                        });
                                    }}
                                    onBlur={() => {
                                        if (validatedForm) {
                                            validateInputsForm();
                                        }
                                    }}
                                    error={!!formErrors.idRateScopeModel}
                                    errorMessage={formErrors.idRateScopeModel}
                                />
                            </GridChild>
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <LoadedOptionsTypeahead
                                    required
                                    disabled={
                                        currencies.isLoading ||
                                        !mainInfoEdition.idSupplierHotel ||
                                        isBlockedContract ||
                                        readOnlyUser
                                    }
                                    labelText='currency'
                                    options={currencies.data || []}
                                    idValue={mainInfoEdition?.idCurrency}
                                    handleChange={(
                                        _event: React.SyntheticEvent,
                                        value: Pick<
                                            ICurrency,
                                            'Id' | 'Name'
                                        > | null
                                    ) => {
                                        handleChangeMainInfoEdition({
                                            idCurrency: value
                                                ? parseInt(value.Id, 10)
                                                : 0,
                                        });
                                    }}
                                    onBlur={() => {
                                        if (validatedForm) {
                                            validateInputsForm();
                                        }
                                    }}
                                    error={!!formErrors.idCurrency}
                                    errorMessage={formErrors.idCurrency}
                                />
                            </GridChild>
                            <GridChild gridProps={full_ColumnGrid}>
                                <TextInput
                                    id='description'
                                    gridProps={defaultDoubleGridProps}
                                    labelText='description2'
                                    value={mainInfoEdition.description}
                                    disabled={
                                        isLoadingPanel ||
                                        isBlockedContract ||
                                        readOnlyUser
                                    }
                                    handleChange={(
                                        e: React.ChangeEvent<HTMLInputElement>
                                    ) =>
                                        handleChangeMainInfoEdition({
                                            description: e.target.value,
                                        })
                                    }
                                    multiline
                                    rows={4}
                                />
                            </GridChild>
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <DateInput
                                    required
                                    labelText='from'
                                    value={mainInfoEdition.activeFrom}
                                    disabled={
                                        isLoadingPanel ||
                                        isBlockedContract ||
                                        readOnlyUser
                                    }
                                    onChange={(date: Date | null) => {
                                        handleChangeMainInfoEdition({
                                            activeFrom: date,
                                        });
                                    }}
                                    onBlur={() => {
                                        if (validatedForm) {
                                            validateInputsForm();
                                        }
                                    }}
                                    minDate={new Date()}
                                    maxDate={
                                        mainInfoEdition?.activeTo || undefined
                                    }
                                    error={!!formErrors.activeFrom}
                                    errorMessage={formErrors.activeFrom}
                                />
                            </GridChild>
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <DateInput
                                    required
                                    labelText='to'
                                    value={mainInfoEdition.activeTo}
                                    disabled={
                                        isLoadingPanel ||
                                        isBlockedContract ||
                                        readOnlyUser
                                    }
                                    onChange={(date: Date | null) => {
                                        handleChangeMainInfoEdition({
                                            activeTo: date,
                                        });
                                    }}
                                    onBlur={() => {
                                        if (validatedForm) {
                                            validateInputsForm();
                                        }
                                    }}
                                    minDate={
                                        mainInfoEdition?.activeFrom || undefined
                                    }
                                    error={!!formErrors.activeTo}
                                    errorMessage={formErrors.activeTo}
                                />
                            </GridChild>
                        </Grid>
                    </div>
                    <div className='flex-end gap-20 padding-top-10'>
                        <Button
                            variant='outlined'
                            onClick={onClose}
                            disabled={isLoadingPanel}
                        >
                            {translate('cancel')}
                        </Button>
                        <Button
                            variant='contained'
                            onClick={async () => {
                                setValidatedForm(true);
                                const validated = validateInputsForm();
                                if (validated) {
                                    await handleSaveMainInfo();
                                }
                            }}
                            disabled={
                                isLoadingPanel ||
                                isBlockedContract ||
                                readOnlyUser
                            }
                        >
                            {translate('save')}
                        </Button>
                    </div>
                </form>
            )}
        </LateralPanel>
    );
};

export default MainInfoPanel;
